import React, { useMemo } from 'react'
import type { Dayjs } from 'dayjs'

import Box from 'components_new/atoms/Box'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SectionHeader from 'components_new/molecules/SectionHeader'

import dayjs from 'utils/dayjs'
import {
  FileDelivery,
  FileDeliveryHistoryEntry,
  JobHistoryEntry,
  JobStatus,
  JobSummary
} from 'redux/selectors/internal/DataRuns'
import {
  deliveryKey,
  deliveryLabel,
  DELIVERY_BORDER_COLOR,
  DELIVERY_FILL_COLOR,
  DELIVERY_STATUS_FILL_COLOR,
  formatIntervalHours,
  jobKey,
  NO_RUN_CELL_COLOR,
  RUNNING_PULSE_SX,
  STATUS_BORDER_COLOR,
  STATUS_FILL_COLOR,
  STATUS_LABEL
} from '../utils'

export interface DataRunsStatusGridProps {
  /**
   * Customer file sources, as their own rows under the job rows. A source
   * delivering roughly daily reads exactly like a job here; a monthly one
   * needs the cadence chip and the expected-day marking below to keep its
   * mostly-empty row from reading as broken.
   */
  fileDeliveries: FileDelivery[]
  jobs: JobSummary[]
  onSelectFileDelivery: (delivery: FileDelivery) => void
  onSelectJobRun: (job: JobSummary) => void
}

const DAY_COUNT = 14
const LABEL_COLUMN_WIDTH = 250

/**
 * `status: null` is the "nothing ran that day" cell. It belongs in the
 * legend because it is the only fill on the grid that is not a job status,
 * and an unexplained grey block reads as a third kind of failure.
 */
const LEGEND_ITEMS: { label: string; status: JobStatus | null }[] = [
  { label: STATUS_LABEL.ok, status: 'ok' },
  { label: STATUS_LABEL.fail, status: 'fail' },
  { label: STATUS_LABEL.timeout, status: 'timeout' },
  { label: STATUS_LABEL.running, status: 'running' },
  { label: 'Ingen körning', status: null }
]

/**
 * The delivery cells' own legend. Separate from the job legend above
 * because the vocabularies do not overlap: a job is never "expected but
 * absent", and a delivery is never a timeout.
 */
const DELIVERY_LEGEND_ITEMS: {
  bg: string
  border: string
  label: string
}[] = [
  {
    bg: DELIVERY_FILL_COLOR.ok,
    border: DELIVERY_BORDER_COLOR.ok,
    label: 'Levererad'
  },
  {
    bg: DELIVERY_FILL_COLOR.fail,
    border: DELIVERY_BORDER_COLOR.fail,
    label: 'Fel i filen'
  },
  {
    bg: NO_RUN_CELL_COLOR.bg,
    border: NO_RUN_CELL_COLOR.border,
    label: 'Ingen leverans'
  }
]

/** The last `DAY_COUNT` calendar days (Europe/Stockholm), oldest first. */
function getLastDays(count: number): Dayjs[] {
  const today = dayjs().tz().startOf('day')

  return Array.from({ length: count }, (_, index) =>
    today.subtract(count - 1 - index, 'day')
  )
}

/**
 * The latest history entry per day (history is newest-first, so the first
 * match for a day is that day's latest run). A day with no matching entry
 * renders empty rather than as a success - a day off is not a success.
 */
function buildDayCells(
  job: JobSummary,
  days: Dayjs[]
): (JobHistoryEntry | null)[] {
  const history = job.history ?? []

  return days.map((day) => {
    const match = history.find((entry) =>
      dayjs(entry.started_at).tz().isSame(day, 'day')
    )

    return match ?? null
  })
}

/**
 * The latest delivery per day, same rule as `buildDayCells` for jobs. A day
 * with no delivery is null - and for a source that is not supposed to
 * deliver daily, that is the normal case rather than a problem, which is
 * what the row's cadence label is there to say.
 */
function buildDeliveryDayCells(
  delivery: FileDelivery,
  days: Dayjs[]
): (FileDeliveryHistoryEntry | null)[] {
  const history = delivery.history ?? []

  return days.map((day) => {
    const match = history.find((entry) =>
      dayjs(entry.delivered_at).tz().isSame(day, 'day')
    )

    return match ?? null
  })
}

/**
 * A cell says one thing only: a delivery arrived that day, or it did not.
 *
 * There is deliberately no third "expected but absent" state. Marking the
 * days a source was overdue looks informative but makes a claim the data
 * does not support: a monthly source overdue by 40 days would have every
 * cell in a 14-day window marked, so one missed delivery renders as
 * fourteen, and a reader counting cells counts misses. It reads correctly
 * for daily sources and wrongly for infrequent ones - which are the ones it
 * would have been for. The cadence label on the row explains a sparse row
 * without claiming a count, and the row's status dot already carries
 * late/missing.
 */
function deliveryCellTitle(
  entry: FileDeliveryHistoryEntry | null,
  dayLabel: string
): string {
  if (!entry) {
    return `${dayLabel} · Ingen leverans`
  }

  return `${dayLabel} · ${entry.status === 'ok' ? 'Levererad' : 'Fel i filen'}`
}

const Legend = () => (
  <Box
    sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5 }}
  >
    {LEGEND_ITEMS.map((item) => (
      <Box
        key={item.label}
        sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}
      >
        <Box
          sx={{
            bgcolor: item.status
              ? STATUS_FILL_COLOR[item.status]
              : NO_RUN_CELL_COLOR.bg,
            border: '1px solid',
            borderColor: item.status
              ? STATUS_BORDER_COLOR[item.status]
              : NO_RUN_CELL_COLOR.border,
            borderRadius: '3px',
            height: '10px',
            width: '10px'
          }}
        />
        <Text sx={{ color: 'text.secondary' }} variant="caption">
          {item.label}
        </Text>
      </Box>
    ))}
  </Box>
)

/**
 * "Status per jobb, 14 dagar" - a 14-day heatmap per job, bucketed by
 * Europe/Stockholm calendar day. Built new alongside the timeline since
 * there is no existing grid/heatmap component in the codebase for this
 * shape of data.
 *
 * v1 caveat: `history` is capped at the latest 14 *runs*, not 14 *days* - a
 * job with multiple runs on the same day will show fewer covered days than
 * a job that runs once a day, even though both list 14 entries.
 */
const DeliveryLegend = () => (
  <Box
    sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5 }}
  >
    {DELIVERY_LEGEND_ITEMS.map((item) => (
      <Box
        key={item.label}
        sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}
      >
        <Box
          sx={{
            bgcolor: item.bg,
            border: '1px solid',
            borderColor: item.border,
            borderRadius: '3px',
            height: '10px',
            width: '10px'
          }}
        />
        <Text sx={{ color: 'text.secondary' }} variant="caption">
          {item.label}
        </Text>
      </Box>
    ))}
  </Box>
)

const DataRunsStatusGrid = (props: DataRunsStatusGridProps) => {
  const { fileDeliveries, jobs, onSelectFileDelivery, onSelectJobRun } = props

  const days = useMemo(() => getLastDays(DAY_COUNT), [])

  const sortedJobs = useMemo(
    () => [...jobs].sort((a, b) => a.job_name.localeCompare(b.job_name)),
    [jobs]
  )

  const sortedDeliveries = useMemo(
    () =>
      [...fileDeliveries].sort((a, b) =>
        deliveryLabel(a).localeCompare(deliveryLabel(b))
      ),
    [fileDeliveries]
  )

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '20px',
        p: '20px 22px'
      }}
    >
      <SectionHeader
        actions={<Legend />}
        body={`${jobs.length} jobb · ${sortedDeliveries.length} filkällor · ${DAY_COUNT} dagar · sista kolumnen är idag`}
        title="Status per jobb och filleverans, 14 dagar"
      />

      {jobs.length === 0 && sortedDeliveries.length === 0 ? (
        <EmptyPlaceholder title="Inga jobb att visa." />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: '4px',
            gridTemplateColumns: `${LABEL_COLUMN_WIDTH}px repeat(${DAY_COUNT}, minmax(0, 1fr))`,
            mt: 2
          }}
        >
          <Box />
          {days.map((day) => (
            <Text
              align="center"
              key={day.toISOString()}
              sx={{
                color: 'text.disabled',
                fontVariantNumeric: 'tabular-nums'
              }}
              variant="caption"
            >
              {day.format('D')}
            </Text>
          ))}

          {sortedJobs.map((job) => {
            const cells = buildDayCells(job, days)

            return (
              <React.Fragment key={jobKey(job)}>
                <Box
                  onClick={() => onSelectJobRun(job)}
                  sx={{
                    alignItems: 'center',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: 1,
                    minWidth: 0
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: STATUS_FILL_COLOR[job.status],
                      borderRadius: '50%',
                      flexShrink: 0,
                      height: '7px',
                      width: '7px'
                    }}
                  />
                  <Text
                    sx={{
                      color:
                        job.status === 'fail' ? 'error.text' : 'text.primary',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    variant="body2"
                  >
                    {job.job_name}
                  </Text>
                </Box>

                {cells.map((entry, index) => {
                  const dayLabel = days[index].format('D MMMM')

                  return (
                    <div
                      key={days[index].toISOString()}
                      title={
                        entry
                          ? `${dayLabel} · ${STATUS_LABEL[entry.status]}`
                          : `${dayLabel} · Ingen körning`
                      }
                    >
                      <Box
                        sx={{
                          bgcolor: entry
                            ? STATUS_FILL_COLOR[entry.status]
                            : NO_RUN_CELL_COLOR.bg,
                          border: '1px solid',
                          borderColor: entry
                            ? STATUS_BORDER_COLOR[entry.status]
                            : NO_RUN_CELL_COLOR.border,
                          borderRadius: '6px',
                          height: '24px',
                          ...(entry?.status === 'running'
                            ? RUNNING_PULSE_SX
                            : null)
                        }}
                      />
                    </div>
                  )
                })}
              </React.Fragment>
            )
          })}

          {sortedDeliveries.map((delivery) => {
            const cells = buildDeliveryDayCells(delivery, days)

            return (
              <React.Fragment key={deliveryKey(delivery)}>
                <Box
                  onClick={() => onSelectFileDelivery(delivery)}
                  sx={{
                    alignItems: 'center',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: 1,
                    minWidth: 0
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: DELIVERY_STATUS_FILL_COLOR[delivery.status],
                      flexShrink: 0,
                      height: '7px',
                      transform: 'rotate(45deg)',
                      width: '7px'
                    }}
                  />
                  <Text
                    sx={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    variant="body2"
                  >
                    {deliveryLabel(delivery)}
                  </Text>
                  {/*
                    Says why a mostly-empty row is empty. Without it a
                    monthly source's single filled cell reads as thirteen
                    missed days.
                  */}
                  <Text
                    sx={{ color: 'text.disabled', flexShrink: 0 }}
                    variant="caption"
                  >
                    {formatIntervalHours(delivery.median_interval_hours)}
                  </Text>
                </Box>

                {cells.map((entry, index) => {
                  const day = days[index]
                  const dayLabel = day.format('D MMMM')

                  return (
                    <div
                      key={day.toISOString()}
                      title={deliveryCellTitle(entry, dayLabel)}
                    >
                      <Box
                        sx={{
                          bgcolor: entry
                            ? DELIVERY_FILL_COLOR[entry.status]
                            : NO_RUN_CELL_COLOR.bg,
                          border: '1px solid',
                          borderColor: entry
                            ? DELIVERY_BORDER_COLOR[entry.status]
                            : NO_RUN_CELL_COLOR.border,
                          borderRadius: '6px',
                          height: '24px'
                        }}
                      />
                    </div>
                  )
                })}
              </React.Fragment>
            )
          })}
        </Box>
      )}

      {sortedDeliveries.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <DeliveryLegend />
        </Box>
      ) : null}
    </Box>
  )
}

DataRunsStatusGrid.displayName = 'DataRunsStatusGrid'
export default DataRunsStatusGrid
